import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { BookingCalendar } from "@/components/BookingCalendar";
import { MiniMonthCalendar } from "@/components/MiniMonthCalendar";
import { SlotRequestPanel } from "@/components/SlotRequestPanel";
import { useRoom } from "@/hooks/useRooms";
import { formatPriceEur } from "@/lib/format";

export function RoomPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: room, isLoading, error } = useRoom(slug);
  const [picked, setPicked] = useState<{ start: Date; end: Date } | null>(null);
  const [calendarDate, setCalendarDate] = useState<Date>(() => new Date());

  if (isLoading) {
    return (
      <div className="mx-auto max-w-container px-6 py-16">
        <p className="text-body-sm text-ink-muted">Raum wird geladen…</p>
      </div>
    );
  }
  if (error || !room) {
    return (
      <div className="mx-auto max-w-container px-6 py-16">
        <p className="mb-4 text-body-sm text-danger">Raum nicht gefunden.</p>
        <Link to="/" className="text-primary underline">
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-container px-6 py-12">
      <Link to="/" className="mb-6 inline-block text-body-sm text-ink-muted hover:text-primary">
        ← Alle Räume
      </Link>

      <header className="mb-10 grid grid-cols-1 gap-8 md:grid-cols-[2fr_1fr]">
        <div>
          {room.location && (
            <p className="mb-2 text-caption font-medium uppercase tracking-widest text-primary">
              {room.location}
            </p>
          )}
          <h1 className="mb-4 font-display text-h1 uppercase text-primary">{room.name}</h1>
          {room.description && (
            <p className="text-lead text-ink-soft">{room.description}</p>
          )}
        </div>
        <aside className="rounded-card border border-line bg-surface p-6 shadow-card">
          <p className="mb-1 text-caption uppercase tracking-wider text-ink-muted">Stundenpreise</p>
          <dl className="mb-4 space-y-1.5 text-body-sm">
            {room.prices.map((offer) => (
              <div key={offer.slug} className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-soft">{offer.name}</dt>
                <dd className="font-display text-h4 text-primary">
                  {offer.price_cents === 0 ? (
                    "kostenlos"
                  ) : (
                    <>
                      {formatPriceEur(offer.price)}
                      <span className="text-body-sm text-ink-muted"> /h</span>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <ul className="space-y-1.5 text-body-sm text-ink-soft">
            <li>Mindestens {room.min_duration_minutes} Min. pro Buchung</li>
            {room.buffer_minutes > 0 && (
              <li>{room.buffer_minutes} Min. Puffer zwischen Buchungen</li>
            )}
            {room.capacity && <li>Bis zu {room.capacity} Personen</li>}
          </ul>
        </aside>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 font-display text-h2 uppercase text-primary">Verfügbarkeit</h2>
        <p className="mb-4 text-body text-ink-soft">
          Freien Slot im Kalender wählen, anschliessend Buchungsanfrage senden. Belegte
          Zeiten sind <span className="font-semibold text-primary">rot</span>, gesperrte
          Zeiten <span className="font-semibold text-ink-muted">grau</span>. Tipp fürs
          Smartphone: Slot einfach antippen und Start/Ende unten anpassen.
        </p>
        <div className="grid gap-4 md:grid-cols-[260px_1fr]">
          <aside className="hidden md:block">
            <MiniMonthCalendar value={calendarDate} onChange={setCalendarDate} />
          </aside>
          <BookingCalendar
            room={room}
            onSelectSlot={setPicked}
            selected={picked}
            date={calendarDate}
            onNavigate={setCalendarDate}
          />
        </div>
      </section>

      <SlotRequestPanel room={room} value={picked} onChange={setPicked} />
    </div>
  );
}
